import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UserProfile, CreditPack } from '../types';
import { doc, updateDoc, setDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import Cropper from 'react-easy-crop';
import { 
  Save, 
  UserCircle, 
  Phone, 
  Mail, 
  BadgeCheck, 
  Loader2, 
  Calendar, 
  Check, 
  Globe, 
  RefreshCw, 
  AlertCircle, 
  Video, 
  Bell, 
  Zap, 
  CreditCard, 
  Plus, 
  Trash2, 
  DollarSign,
  Coins,
  Camera,
  X,
  Image as ImageIcon,
  ArrowDownToLine
} from 'lucide-react';
import { fetchUserCalendars, reconnectGoogle, GoogleAuthError } from '../lib/googleCalendar';
import { useTranslation } from '../lib/translations';
import Avatar from '../components/Avatar';

interface ProfileProps {
  profile: UserProfile | null;
  onUpdate: (newProfile: UserProfile) => void;
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'Fr.', name: 'Swiss Franc' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
];

const Profile: React.FC<ProfileProps> = ({ profile, onUpdate }) => {
  const [firstName, setFirstName] = useState(profile?.firstName || '');
  const [lastName, setLastName] = useState(profile?.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(profile?.phoneNumber || '');
  // Fixed: setGoogleCalendarId used before its declaration, changed to useState
  const [googleCalendarId, setGoogleCalendarId] = useState(profile?.googleCalendarId || '');
  const [googleCalendarSyncEnabled, setGoogleCalendarSyncEnabled] = useState(profile?.googleCalendarSyncEnabled ?? false);
  const [currency, setCurrency] = useState(profile?.currency || 'EUR');
  
  // Automation settings
  const [googleMeetEnabled, setGoogleMeetEnabled] = useState(profile?.googleMeetEnabled ?? true);
  const [notificationEnabled, setNotificationEnabled] = useState(profile?.notificationEnabled ?? true);
  const [notificationMinutes, setNotificationMinutes] = useState(profile?.notificationMinutes ?? 10);

  // Credit Packs
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>(profile?.creditPacks || []);
  const [newPackName, setNewPackName] = useState('');
  const [newPackCredits, setNewPackCredits] = useState(5);
  const [newPackPrice, setNewPackPrice] = useState(150);

  const [calendars, setCalendars] = useState<any[]>([]);
  const [fetchingCalendars, setFetchingCalendars] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [authError, setAuthError] = useState(false);
  const { t } = useTranslation();

  // Image Upload States
  const [imageFile, setImageFile] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentCurrency = CURRENCIES.find(c => c.code === currency) || CURRENCIES.find(c => c.code === 'EUR') || CURRENCIES[0];

  const hasToken = !!sessionStorage.getItem('google_access_token');

  useEffect(() => {
    if (profile?.role === 'teacher') {
      loadCalendars();
    }
  }, [profile]);

  const loadCalendars = async () => {
    setFetchingCalendars(true);
    setAuthError(false);
    try {
      const list = await fetchUserCalendars();
      setCalendars(list);
    } catch (e) {
      if (e instanceof GoogleAuthError && e.status === 401) {
        setAuthError(true);
      }
    } finally {
      setFetchingCalendars(false);
    }
  };

  const handleReconnect = async () => {
    const token = await reconnectGoogle();
    if (token) {
      loadCalendars();
    }
  };

  const handleAddPack = () => {
    if (!newPackName.trim()) return;
    const pack: CreditPack = {
      id: Math.random().toString(36).substr(2, 9),
      name: newPackName,
      credits: newPackCredits,
      price: newPackPrice,
      currency: currency
    };
    setCreditPacks([...creditPacks, pack]);
    setNewPackName('');
  };

  const handleRemovePack = (id: string) => {
    setCreditPacks(creditPacks.filter(p => p.id !== id));
  };

  // Image Upload & Cropping Logic
  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageFile(reader.result as string);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedBase64 = async (imageSrc: string, pixelCrop: any): Promise<string | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    // Set max size to ensure compression and low document weight in Firestore
    const outputSize = 400;
    canvas.width = outputSize;
    canvas.height = outputSize;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputSize,
      outputSize
    );

    // Return as Base64 string
    return canvas.toDataURL('image/jpeg', 0.7);
  };

  const handleUploadAvatar = async () => {
    if (!profile || !imageFile || !croppedAreaPixels) return;

    setIsUploading(true);
    try {
      const base64Image = await getCroppedBase64(imageFile, croppedAreaPixels);
      if (!base64Image) throw new Error("Failed to process image");

      // Use setDoc with merge for more robust writes
      await setDoc(doc(db, 'users', profile.uid), { photoURL: base64Image }, { merge: true });
      
      onUpdate({ ...profile, photoURL: base64Image });
      setIsCropping(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      console.error(e);
      alert('Failed to update profile photo. Please try again.');
    }
    setIsUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true); setSuccess(false);
    try {
      const isTeacher = profile.role === 'teacher';
      
      // Conditionally build updates to avoid sending teacher-specific fields for student users
      const updates: any = { 
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`.trim(),
        phoneNumber,
        notificationEnabled,
        notificationMinutes
      };

      if (isTeacher) {
        updates.googleCalendarId = googleCalendarId;
        updates.googleCalendarSyncEnabled = googleCalendarSyncEnabled;
        updates.googleMeetEnabled = googleMeetEnabled;
        updates.creditPacks = creditPacks;
        updates.currency = currency;
      }

      // Use setDoc with merge for more robust writes
      await setDoc(doc(db, 'users', profile.uid), updates, { merge: true });
      
      onUpdate({ ...profile, ...updates });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) { 
      console.error(e);
      alert(t.profile.updateFailed); 
    }
    setSaving(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-20">
      <div>
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{t.profile.title}</h1>
        <p className="text-slate-500 font-medium">{t.profile.desc}</p>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl overflow-hidden shadow-2xl">
        <div className="h-32 bg-gradient-to-r from-purple-700 via-indigo-700 to-indigo-900 relative">
          <div className="absolute -bottom-10 left-10 group">
            <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <Avatar 
                src={profile?.photoURL} 
                name={`${firstName} ${lastName}`} 
                className="w-24 h-24 rounded-xl border-4 border-white dark:border-slate-900 shadow-2xl" 
                iconSize={40}
              />
              <div className="absolute inset-0 bg-slate-950/40 rounded-xl opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera size={24} className="text-white" />
              </div>
              {isUploading && (
                <div className="absolute inset-0 bg-slate-950/60 rounded-xl flex items-center justify-center">
                  <Loader2 className="animate-spin text-white" size={24} />
                </div>
              )}
            </div>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*" 
              onChange={handleFileChange} 
            />
          </div>
          <button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-4 right-10 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 rounded-lg text-[10px] font-black uppercase tracking-widest text-white transition-all shadow-lg"
          >
            <ImageIcon size={14} /> Change Photo
          </button>
        </div>
        
        <div className="pt-14 p-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="px-3 py-1 rounded-md bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest">{profile?.role === 'teacher' ? t.common.teacher : t.common.student}</span>
            <div className="flex items-center gap-1.5 text-slate-400 text-xs font-bold">
              <BadgeCheck size={16} className="text-purple-600" /> {t.profile.personalInfo}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <UserCircle size={14} /> {t.profile.firstName}
                </label>
                <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-bold focus:ring-4 focus:ring-purple-500/10 outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <UserCircle size={14} /> {t.profile.lastName}
                </label>
                <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-bold focus:ring-4 focus:ring-purple-500/10 outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <Phone size={14} /> {t.profile.phoneNumber}
                </label>
                <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-bold focus:ring-4 focus:ring-purple-500/10 outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <Mail size={14} /> {t.profile.loginEmail}
                </label>
                <input type="email" value={profile?.email} disabled className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm text-slate-400 cursor-not-allowed font-medium" />
              </div>
              {profile?.role === 'teacher' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <Coins size={14} /> {t.profile.currency}
                  </label>
                  <select 
                    value={currency} 
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-bold focus:ring-4 focus:ring-purple-500/10 outline-none transition-all"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.code} ({c.symbol}) - {c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {profile?.role === 'teacher' && (
              <>
                <div className="pt-10 border-t border-slate-100 dark:border-white/5 space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex items-center justify-center text-teal-600">
                      <CreditCard size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{t.profile.creditPacksTitle}</h3>
                      <p className="text-xs font-medium text-slate-500">{t.profile.creditPacksDesc.replace('{currency}', currency)}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-12 gap-3 items-end bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-white/5">
                      <div className="col-span-5 space-y-1">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">{t.profile.packName}</label>
                        <input type="text" placeholder="e.g. 10 Lessons Pack" value={newPackName} onChange={(e) => setNewPackName(e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-3 py-2 text-xs font-bold" />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">{t.common.credits}</label>
                        <input type="number" value={newPackCredits} onChange={(e) => setNewPackCredits(parseInt(e.target.value) || 0)} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-3 py-2 text-xs font-bold" />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">{t.profile.price} ({currentCurrency.symbol})</label>
                        <div className="relative">
                           <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-black">{currentCurrency.symbol}</span>
                           <input type="number" value={newPackPrice} onChange={(e) => setNewPackPrice(parseInt(e.target.value) || 0)} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded pl-6 pr-3 py-2 text-xs font-bold" />
                        </div>
                      </div>
                      <div className="col-span-1">
                        <button type="button" onClick={handleAddPack} className="w-full aspect-square bg-purple-600 text-white rounded flex items-center justify-center hover:bg-purple-700 transition-all shadow-lg shadow-purple-500/20">
                          <Plus size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {creditPacks.map((pack) => (
                        <div key={pack.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-slate-100 dark:border-white/5 rounded-xl shadow-sm group">
                          <div>
                            <p className="text-xs font-black uppercase tracking-tight text-slate-900 dark:text-white">{pack.name}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{pack.credits} {t.common.credits} • {currentCurrency.symbol}{pack.price}</p>
                          </div>
                          <button type="button" onClick={() => handleRemovePack(pack.id)} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                      {creditPacks.length === 0 && (
                        <div className="sm:col-span-2 py-8 text-center border-2 border-dashed border-slate-100 dark:border-white/5 rounded-xl">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">{t.profile.noPacks}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-10 border-t border-slate-100 dark:border-white/5 space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center text-indigo-600">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{t.profile.googleSyncTitle}</h3>
                        <p className="text-xs font-medium text-slate-500">{t.profile.googleSyncDesc}</p>
                      </div>
                    </div>
                    {fetchingCalendars && <Loader2 className="animate-spin text-indigo-600" size={20} />}
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-white/5 rounded-xl p-6 space-y-6">
                    {(!hasToken || authError) ? (
                      <div className="flex flex-col items-center justify-center py-6 text-center space-y-4 animate-in fade-in">
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-full">
                          <AlertCircle size={32} />
                        </div>
                        <div>
                          <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">{t.profile.connectionExpired}</h4>
                          <p className="text-xs text-slate-500 mt-1 max-w-[300px]">{t.profile.expiredDesc}</p>
                        </div>
                        <button 
                          type="button" 
                          onClick={handleReconnect}
                          className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
                        >
                          <RefreshCw size={14} /> {t.profile.reconnect}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block ml-1">{t.profile.targetCalendar}</label>
                          <select 
                            value={googleCalendarId} 
                            onChange={(e) => setGoogleCalendarId(e.target.value)}
                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg py-2.5 px-4 text-sm font-bold focus:ring-4 focus:ring-indigo-500/10 outline-none appearance-none cursor-pointer"
                          >
                            <option value="">{t.profile.syncDisabled}</option>
                            {calendars.map(cal => (
                              <option key={cal.id} value={cal.id}>{cal.summary} {cal.primary ? `(${t.common.primary})` : ''}</option>
                            ))}
                          </select>
                        </div>

                        {googleCalendarId && (
                          <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg">
                                  <ArrowDownToLine size={16} />
                                </div>
                                <div>
                                  <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-900 dark:text-white">{t.profile.googleImportTitle}</h4>
                                  <p className="text-[10px] text-slate-500 font-medium">{t.profile.googleImportDesc}</p>
                                </div>
                              </div>
                              <button 
                                type="button" 
                                onClick={() => setGoogleCalendarSyncEnabled(!googleCalendarSyncEnabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${googleCalendarSyncEnabled ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-800'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${googleCalendarSyncEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </div>
                          </div>
                        )}

                        {googleCalendarId && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-teal-500/10 text-teal-600 rounded-lg text-[10px] font-black uppercase tracking-widest animate-in fade-in">
                            <Globe size={12} /> {t.profile.syncEnabled}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-10 border-t border-slate-100 dark:border-white/5 space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center text-purple-600">
                      <Zap size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{t.profile.automationTitle}</h3>
                      <p className="text-xs font-medium text-slate-500">{t.profile.automationDesc}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-6 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-white/5 rounded-xl space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                          <Video size={14} className="text-indigo-600" /> {t.profile.defaultMeet}
                        </label>
                        <button 
                          type="button" 
                          onClick={() => setGoogleMeetEnabled(!googleMeetEnabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${googleMeetEnabled ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-800'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${googleMeetEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-500 font-medium">{t.profile.defaultMeetDesc}</p>
                    </div>

                    <div className="p-6 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-white/5 rounded-xl space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                          <Bell size={14} className="text-amber-500" /> {t.profile.notifications}
                        </label>
                        <button 
                          type="button" 
                          onClick={() => setNotificationEnabled(!notificationEnabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationEnabled ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-800'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {notificationEnabled && (
                        <div className="flex items-center gap-2 animate-in slide-in-from-top-1">
                          <input 
                            type="number" 
                            value={notificationMinutes} 
                            onChange={(e) => setNotificationMinutes(parseInt(e.target.value) || 0)}
                            className="w-20 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-2 py-1 text-xs font-black"
                          />
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.profile.minutesBefore}</span>
                        </div>
                      )}
                      <p className="text-[10px] text-slate-500 font-medium">{t.profile.notificationsDesc}</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="pt-8 flex items-center justify-between">
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                {profile?.role === 'teacher' ? t.profile.secureConnectionTeacher : t.profile.secureConnectionStudent}
              </p>
              <button type="submit" disabled={saving} className={`flex items-center gap-2 px-10 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${success ? 'bg-teal-600 text-white' : 'bg-purple-600 text-white hover:bg-purple-700 shadow-xl shadow-purple-500/20'}`}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : success ? <Check size={16} /> : <Save size={16} />} 
                {success ? t.common.done + '!' : t.profile.updateProfile}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Cropping Modal */}
      {isCropping && imageFile && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300 !mt-0">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl border border-slate-200 dark:border-white/5 shadow-2xl flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
              <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Crop Profile Photo</h2>
              <button onClick={() => setIsCropping(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all"><X size={24} /></button>
            </div>
            
            <div className="relative h-96 w-full bg-slate-100 dark:bg-slate-950">
              <Cropper
                image={imageFile}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </div>

            <div className="p-8 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <span>Zoom</span>
                  <span>{Math.round(zoom * 100)}%</span>
                </div>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e: any) => setZoom(e.target.value)}
                  className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setIsCropping(false)}
                  className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl font-black text-[11px] uppercase tracking-widest transition-all hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUploadAvatar}
                  disabled={isUploading}
                  className="flex-2 px-10 py-4 bg-purple-600 text-white rounded-xl font-black text-[11px] uppercase tracking-widest transition-all shadow-xl shadow-purple-500/20 hover:bg-purple-700 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isUploading ? <Loader2 className="animate-spin" size={16} /> : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;